import { useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useAdminRoster } from "@/hooks/useAdminRoster";
import { buildDeck, type Deck } from "@/lib/brain/deck";
import type { BrainCategory, BrainItem } from "@/lib/brain/types";
import { useBrainState } from "./useBrainState";
import {
  useBrainOwnerItems,
  useBrainPaymentItems,
  useBrainRefresh,
  useBrainSupportItems,
  useBrainTenantItems,
} from "./useBrainSources";
import { useBerlinDay } from "./useBerlinDay";

/** Each category hook returns memoized { items, isLoading, isError }. */
function useBrainItems(enabled: boolean): {
  items: BrainItem[];
  isLoading: boolean;
  failedCategories: BrainCategory[];
} {
  const tenants = useBrainTenantItems(enabled);
  // Late rent reads the tenants' bookings, so it follows their readiness.
  const payments = useBrainPaymentItems(enabled, tenants.bookings);
  const owners = useBrainOwnerItems(enabled);
  const support = useBrainSupportItems(enabled);
  const items = useMemo(
    () => [
      ...payments.items,
      ...tenants.items,
      ...owners.items,
      ...support.items,
    ],
    [payments.items, tenants.items, owners.items, support.items]
  );
  const failedCategories = useMemo(() => {
    const failed: BrainCategory[] = [];
    if (payments.isError) failed.push("payments");
    if (tenants.isError) failed.push("tenants");
    // One read feeds both: owner cards and the property gaps.
    if (owners.isError) failed.push("owners", "properties");
    if (support.isError) failed.push("support");
    return failed;
  }, [payments.isError, tenants.isError, owners.isError, support.isError]);
  return {
    items,
    isLoading:
      payments.isLoading ||
      tenants.isLoading ||
      owners.isLoading ||
      support.isLoading,
    failedCategories,
  };
}

export function useBrainDeck(enabled = true) {
  const { user } = useAuth();
  const me = user?.id ?? "";
  const day = useBerlinDay();
  const state = useBrainState(enabled ? me : undefined);
  const sources = useBrainItems(enabled);
  const refresh = useBrainRefresh();
  const roster = useAdminRoster(enabled);
  // Only a loaded roster can say an assignee left; until then trust them all.
  const rosterReady = !roster.isLoading && !roster.error;
  const knownAssignees = useMemo(
    () =>
      rosterReady ? new Set(roster.roster.map((p) => String(p.id))) : undefined,
    [rosterReady, roster.roster]
  );
  const deck: Deck = useMemo(
    () =>
      buildDeck({
        items: sources.items,
        states: state.states,
        acknowledged: state.acknowledged,
        me,
        now: new Date(),
        knownAssignees,
      }),
    // `day` re-ages cards (escalation) after Berlin midnight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sources.items, state.states, state.acknowledged, me, day, knownAssignees]
  );
  return {
    deck,
    isLoading: sources.isLoading || state.isLoading,
    failedCategories: sources.failedCategories,
    refresh,
    state,
    me,
  };
}

// ponytail: the badge derives the whole deck client-side on every staff admin
// page. Every source it reads polls on BRAIN_POLL (2-min staleTime and
// refetch), so navigating reuses the cache; the first page of a session still
// loads every source. Move to a count RPC if that first load feels slow.
/**
 * `count` is null while the deck is loading: no partial number that jumps.
 * `incomplete`: a category failed, so the count may be low — never show it bare.
 */
export function useBrainBadgeCount(enabled: boolean): {
  count: number | null;
  incomplete: boolean;
} {
  const { deck, failedCategories, isLoading } = useBrainDeck(enabled);
  if (!enabled) return { count: null, incomplete: false };
  return {
    count: isLoading ? null : deck.counts.all,
    incomplete: failedCategories.length > 0,
  };
}
